import React, { useState, useEffect, useContext, useMemo } from 'react'
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  ProgressBar,
} from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { Document, Page, pdfjs } from 'react-pdf'
import { AuthContext } from '../AuthContext'
import axios from 'axios'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import Cookies from 'js-cookie'
import { Helmet } from 'react-helmet'

// 設定 PDF.js 工作者路徑
pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs'

const DKABioReport = () => {
  const { patient_id } = useParams()
  const navigate = useNavigate()
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const [reports, setReports] = useState([])
  const [selectedReport, setSelectedReport] = useState(null)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedType, setSelectedType] = useState('full')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(7)
  const [scale, setScale] = useState(1.2)
  const [patientName, setPatientName] = useState('')
  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}
  const getPatientInfo = '/api/crm/project/patient/getPatientInfo'

  // 獲取所有可用的日期
  const availableDates = useMemo(() => {
    const dates = [
      ...new Set(reports.map((report) => report.check_time.split(' ')[0])),
    ]
    return dates.sort((a, b) => new Date(b) - new Date(a))
  }, [reports])

  // 根據選擇的日期篩選報告
  const filteredReportsByDate = useMemo(() => {
    if (!selectedDate) return reports
    return reports.filter((report) =>
      report.check_time.startsWith(selectedDate)
    )
  }, [reports, selectedDate])

  // 判斷報告類型是否可選
  const typeAvailability = useMemo(() => {
    if (!selectedDate) return { full: true, light: true }

    const hasFullVersion = filteredReportsByDate.some(
      (report) => !report.name.toLowerCase().includes('light')
    )
    const hasLightVersion = filteredReportsByDate.some((report) =>
      report.name.toLowerCase().includes('light')
    )

    return {
      full: hasFullVersion,
      light: hasLightVersion,
    }
  }, [filteredReportsByDate, selectedDate])

  useEffect(() => {
    // 取得姓名
    const fetchPatientInfo = async () => {
      try {
        setIsLoading(true)

        if (!session) {
          setLoginShow(true)
          throw new Error('Session not found or expired')
        }
        const response = await axios.post(
          baseUrl + getPatientInfo,
          { patient_id },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              session: currentUser?.session || session || '',
            },
          }
        )

        // console.log('fetchPatientData', response)

        if (response.data?.name) {
          extendCookie()
          setPatientName(response.data.name)
        } else if (response.data?.error?.code === '50004') {
          alert('登入已逾時，請重新登入')
          setLoginShow(true)
        }
      } catch (error) {
        console.error('Error fetching patient data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    const fetchReports = async () => {
      try {
        setIsLoading(true)

        if (!session) {
          setLoginShow(true)
          throw new Error('Session not found or expired')
        }

        const response = await axios.post(
          `${baseUrl}/api/crm/patient/report/getAnalyzeReport`,
          { patient_id },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              session: currentUser?.session || session || '',
            },
          }
        )

        if (response.data.data) {
          extendCookie()
          if (response.data.data.length === 0) {
            setError('尚無DKABio報告')
            return
          }

          const sortedReports = response.data.data.sort(
            (a, b) => new Date(b.check_time) - new Date(a.check_time)
          )
          setReports(sortedReports)

          if (sortedReports.length > 0) {
            const latestDate = sortedReports[0].check_time.split(' ')[0]
            setSelectedDate(latestDate)
            setSelectedReport(sortedReports[0])
            setSelectedType(
              sortedReports[0].name.toLowerCase().includes('light')
                ? 'light'
                : 'full'
            )
          }
          // 登入狀態不存在
        } else if (response.data.error?.code === '50004') {
          setLoginShow(true)
          throw new Error('Session expired')
          // 病人不存在
        } else if (response.data.error?.code === '10089') {
          setError('尚無DKABio報告')
          return
        }
      } catch (err) {
        setError('無法取得報告資料')
        console.error('Error fetching reports:', err)
      } finally {
        setIsLoading(false)
      }
    }
    if (session) {
      fetchReports()
      fetchPatientInfo()
    } else {
      setLoginShow(true)
    }
  }, [patient_id, baseUrl, currentUser])

  const handleDateChange = (date) => {
    setSelectedDate(date)
    const reportsForDate = reports.filter((report) =>
      report.check_time.startsWith(date)
    )

    if (reportsForDate.length > 0) {
      setSelectedReport(reportsForDate[0])
      setSelectedType(
        reportsForDate[0].name.toLowerCase().includes('light')
          ? 'light'
          : 'full'
      )
      if (!reportsForDate[0].name.toLowerCase().includes('light')) {
        setPageNumber(7)
      } else {
        setPageNumber(1)
      }
      // Reset to first page
    }
  }

  const handleTypeChange = (type) => {
    setSelectedType(type)
    const availableReports = filteredReportsByDate.filter((report) => {
      const isLight = report.name.toLowerCase().includes('light')
      return type === 'light' ? isLight : !isLight
    })

    if (availableReports.length > 0) {
      setSelectedReport(availableReports[0])
      if (type === 'full') {
        setPageNumber(7)
      } else {
        setPageNumber(1)
      }
    }
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const zoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.2, 2.0))
  }

  const zoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.5))
  }

  const goToPrevPage = () => {
    setPageNumber((prevPage) => Math.max(prevPage - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prevPage) => Math.min(prevPage + 1, numPages))
  }

  if (isLoading) {
    return (
      <Container className="text-center">
        <div className="m-5">
          <ProgressBar className="m-4" animated now={100} />
          <h5 className="text-secondary">資料等待中...</h5>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>DKABio 報告 | HA! CRM</title>
        </Helmet>
        <Container className="py-4">
          <div className="mb-4">
            <Button
              variant="link"
              onClick={handleGoBack}
              className="text-decoration-none text-secondary"
            >
              <h5>
                <i className="fa-solid fa-arrow-left me-2" />
                返回{patientName}的個人頁面
              </h5>
            </Button>
          </div>
          <div className="text-center mt-5">
            <h5 className="text-secondary">{error}</h5>
          </div>
        </Container>
      </>
    )
  }

  return (
    <div className="dkabioReport">
      <Helmet>
        <title>DKABio 報告 | HA! CRM</title>
      </Helmet>
      <Container className="py-4">
        <div className="mb-4">
          <Button
            variant="link"
            onClick={handleGoBack}
            className="text-decoration-none text-secondary"
          >
            <h5>
              <i className="fa-solid fa-arrow-left me-2" />
              返回{patientName}的個人頁面
            </h5>
          </Button>
        </div>

        {/* 下拉選單 */}
        <Row className="mb-4">
          <Col md={4}>
            <Form.Select
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="filter-select"
            >
              {availableDates.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Select
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="filter-select"
            >
              <option value="full" disabled={!typeAvailability.full}>
                DKABio 完整版
              </option>
              <option value="light" disabled={!typeAvailability.light}>
                DKABio 精要版
              </option>
            </Form.Select>
          </Col>
        </Row>
        {selectedReport && (
          <>
            {/* 控制按鈕區 */}
            <div className="pdf-controls">
              <div className="pdf-navigation">
                <Button
                  variant="outline-secondary"
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </Button>
                <span className="mx-3">
                  第 {pageNumber} 頁，共 {numPages || '--'} 頁
                </span>
                <Button
                  variant="outline-secondary"
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </Button>
              </div>
              {patientName}的 DKABio 報告
              <div className="pdf-zoom">
                <Button variant="outline-secondary" onClick={zoomOut}>
                  <i className="fa-solid fa-minus"></i>
                </Button>
                <span className="mx-2">{Math.round(scale * 100)}%</span>
                <Button variant="outline-secondary" onClick={zoomIn}>
                  <i className="fa-solid fa-plus"></i>
                </Button>
              </div>
            </div>

            <div className="d-flex text-center justify-content-center">
              {/* PDF 查看器 */}
              <Document
                file={selectedReport.link}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Spinner animation="border" />}
                error="無法載入 PDF"
              >
                <h4 className="m-2">{selectedReport.name}</h4>
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={scale}
                />
              </Document>
            </div>
          </>
        )}
      </Container>
    </div>
  )
}

export default DKABioReport
